import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure, Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { idempotencyKey } from '@/lib/idempotency'
import { initials } from '@/lib/format'
import { safeStorage } from '@/lib/storage'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Channels = Schema<'ChannelsResponse'>
type Channel = Schema<'ChannelOut'>
type Conversation = Schema<'ConversationOut'>
type CategoryFilter = Schema<'ReplyCategoryFilter'>
type Category = Schema<'ReplyCategory'>
type Thread = Schema<'ThreadResponse'>
type Message = Schema<'MessageOut'>

/**
 * One page of conversations, and one page of a thread.
 *
 * Stated here rather than left to the server's default so the "Older" control
 * and the page it pages through are the same size by construction. 25 is the
 * server's own default (`app/v1/inbox.DEFAULT_PAGE`); its ceiling is 100.
 */
const PAGE_SIZE = 25

/**
 * `outreach#inbox`, `#inbox-one` and `#inbox-zero` — one screen, three states.
 *
 * S-D4 drew them as three screens and they are one component, because they are
 * one state machine and the transition between them is a fact the server
 * reports rather than a route: **how many channels are connected.** Zero
 * replaces the inbox with the setup list, one hides the per-message channel
 * labels because they would be noise, and two or more shows them. Routing
 * between three pages would mean a tenant who connects their first channel has
 * to find a different URL for the inbox they were already looking at.
 *
 * **One thread per person, with the channel as a message label.** That is the
 * whole model and the server does the merging (`app/db/inbox.py`) — AugStash
 * has one thread per contact *per channel*, so a lead who replies on WhatsApp
 * after an email arrives here as two provider threads and one conversation.
 * Nothing in this file knows that; it renders what it is given, which is the
 * point of doing the merge on the server.
 *
 * **Replying is a permission this screen may not hold.** `inbox:read` is every
 * role's and `inbox:reply` stops at admin, because a reply is an outbound
 * message to somebody outside the organisation in its name (§8). A member sees
 * the conversation and no composer — not a composer that fails on submit.
 *
 * **The channel labels are not decoration.** A message that went out on SMS and
 * one that went out on WhatsApp cost different money and reach different
 * people, and the tenant is the one who has to know which. So the label follows
 * the message, and it disappears only when there is nothing to distinguish.
 */
export function InboxPage(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const toast = useToast()
  const replies = may(membership, 'inbox:reply')
  // G18 and G19. Setting a thread's category and moving a lead are both
  // `outreach:manage`, which stops at admin — the permission's own docstring
  // argues why a decision the ladder acts on is not a note on a message.
  const manages = may(membership, 'outreach:manage')

  const [search, setSearch] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [category, setCategory] = useState<CategoryFilter | ''>('')

  // OUTR-30. `LeadDetail`'s *Open conversation* carries the id it means, because
  // this screen's default — open the newest — is the wrong thread for somebody
  // who arrived from one particular prospect. Read once as the initial
  // selection: the thread pane fetches by id (`ThreadPane`), so a deep link
  // works even for a conversation that is not on the first page of the list.
  const [params] = useSearchParams()
  const requested = params.get('conversation')
  const [selected, setSelected] = useState<string | null>(requested)

  // WEB-1. The endpoint has always been cursor-paginated and this screen passed
  // no cursor and never read `next_cursor`, so a tenant with more than 25
  // conversations could reach the rest only if the search box happened to match
  // them. A stack rather than a single cursor because a one-way "Older" with no
  // way back is a trap: `Leads.tsx` pages the same way, and popping the stack is
  // the only correct "Newer" when the cursor is opaque and keyset-based.
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null])
  const cursor = cursors[cursors.length - 1] ?? null

  // Every filter change starts a new run. A cursor is a position in one
  // ordering, so carrying it across a filter change asks the server to continue
  // from a row that may not be in the new result set at all.
  const restart = useCallback(() => {
    setCursors([null])
  }, [])

  const channels = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/inbox/channels', { signal })),
    [],
  )
  // `search` and `unreadOnly` are in the dependency list, so typing re-runs the
  // query. Deliberately not debounced here: the query hook aborts the previous
  // request on every change (`useQuery`), so a fast typist produces one
  // in-flight request rather than a queue of them.
  const list = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/conversations', {
          // `null` rather than `undefined` for an empty search: the generated
          // types are checked with `exactOptionalPropertyTypes`, so `undefined`
          // is not assignable to `string | null` — and the server reads both as
          // "no search" (`search or None` at the boundary).
          params: {
            query: {
              search: search || null,
              unread: unreadOnly,
              category: category || null,
              limit: PAGE_SIZE,
              cursor,
            },
          },
          signal,
        }),
      ),
    [search, unreadOnly, category, cursor],
  )

  // Memoised, and not for speed: the array is the dependency of the effect
  // below, so a fresh `[]` on every render would re-run the "open the first
  // conversation" effect for ever.
  const conversations: Conversation[] = useMemo(
    () => (list.state.status === 'ready' ? list.state.data.conversations : []),
    [list.state],
  )

  // The first conversation is opened for you. A two-pane inbox whose right pane
  // is empty on arrival makes the tenant click before they can read anything,
  // and there is exactly one sensible first choice: the newest.
  //
  // It also has to *follow* the list, which it did not before WEB-1 gave the
  // list more than one page: paging to older conversations, or filtering the
  // selected one away, otherwise leaves the right pane showing a thread that is
  // no longer on the left. Guarded on `ready` because `conversations` is `[]`
  // while a refetch is in flight, and clearing the selection on every keystroke
  // of the search box would close the thread the tenant is reading.
  const ready = list.state.status === 'ready'
  useEffect(() => {
    if (!ready) return
    // A thread asked for by URL is held even when the list does not contain it —
    // page five of the inbox is still that prospect's conversation, and
    // replacing it with the newest would answer a different question than the
    // one the link asked. The moment the reader picks another thread, `selected`
    // stops matching `requested` and the rule below takes over again.
    if (requested !== null && selected === requested) return
    const first = conversations[0]
    if (!first) {
      setSelected(null)
      return
    }
    if (selected === null || !conversations.some((row) => row.id === selected)) {
      setSelected(first.id)
    }
  }, [ready, conversations, selected, requested])

  if (channels.state.status === 'loading') return <Loading label="Loading your inbox" lines={4} />
  if (channels.state.status === 'error') {
    return <ErrorState failure={channels.state.failure} onRetry={channels.refetch} />
  }

  const connected: Channels = channels.state.data

  // Parity P-INBX-01: `outreach.html#inbox-zero` is the centred card and
  // nothing above it — no page heading. With no channel connected the `h1` is
  // visually hidden, so the screen keeps its top-level heading for a screen
  // reader and draws what the mockup draws.
  const zero = connected.connected.length === 0
  return (
    <div className="ar-stack ar-stack--loose">
      {zero ? (
        <h1 className="ar-visually-hidden">Inbox</h1>
      ) : (
        <div className="ar-row" style={{ justifyContent: 'space-between' }}>
          <h1 className="ar-h1">Inbox</h1>
          <SyncButton
            onDone={() => {
              list.refetch()
            }}
          />
        </div>
      )}

      {zero ? (
        <SetupList channels={connected.channels} />
      ) : (
        <Inbox
          conversations={conversations}
          channels={connected.channels}
          connected={connected.connected}
          loading={list.state.status === 'loading'}
          failure={list.state.status === 'error' ? list.state.failure : null}
          onRetry={list.refetch}
          search={search}
          onSearch={(value) => {
            setSearch(value)
            restart()
          }}
          unreadOnly={unreadOnly}
          onUnreadOnly={(value) => {
            setUnreadOnly(value)
            restart()
          }}
          category={category}
          onCategory={(value) => {
            setCategory(value)
            restart()
          }}
          hasNewer={cursors.length > 1}
          hasOlder={list.state.status === 'ready' && (list.state.data.next_cursor ?? null) !== null}
          onNewer={() => {
            setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))
          }}
          onOlder={() => {
            if (list.state.status !== 'ready') return
            const next: string | null = list.state.data.next_cursor ?? null
            if (next === null) return
            setCursors((stack) => [...stack, next])
          }}
          canManage={manages}
          selected={selected}
          onSelect={setSelected}
          canReply={replies}
          tenantId={tenant.id}
          onChanged={() => {
            list.refetch()
          }}
          onSent={() => {
            toast.show({ kind: 'success', title: 'Reply sent' })
          }}
        />
      )}
    </div>
  )
}

/**
 * The zero state (S-D4 `#inbox-zero`) — a setup list, not an apology.
 *
 * Every channel §5 names, with what it unlocks and how long it takes, in the
 * order that gets somebody sending soonest. The copy is the server's
 * (`app/outreach/channels.py`) so a channel that becomes available in a later
 * session appears here with no change to this file — the same reason the usage
 * screen's meter labels come from the server.
 */
function SetupList({ channels }: { channels: Channel[] }): JSX.Element {
  // OUTR-77 / OUTR-112. The design puts the primary on the row that gets
  // somebody sending soonest, and the catalogue is *already ordered by that*
  // (`app/outreach/channels.ChannelSpec.order` — "how fast a tenant can be
  // sending on it, explicitly not alphabetical"). So the rule is "the first row
  // with something to do", read off the order rather than hard-coded to email:
  // the day a faster channel is added, the primary moves with it and this file
  // does not change.
  const first = channels.find(canAct)?.channel
  // The sentence is about replies, so the channel it names must be able to
  // carry one — LinkedIn can be the first row to act on and still never fill
  // an inbox.
  const actionable = channels.find((channel) => canAct(channel) && channel.sendable)
  // Held but unable to receive — LinkedIn, on the live tenant. The title said
  // *No channels connected yet* over a row for the account they had just
  // connected (walkthrough 2026-09-12); a held connection is named as held.
  const held = channels.filter((channel) => channel.linked && !channel.connected)

  return (
    <div style={{ maxWidth: '44rem', margin: '0 auto', width: '100%' }}>
      <section className="ar-card">
        <div className="ar-card__body">
          <Empty
            icon="✉"
            title={
              held.length > 0
                ? `${held.map((channel) => channel.label).join(' and ')} ${held.length === 1 ? 'is' : 'are'} connected — nothing can receive a reply yet`
                : 'No channels connected yet'
            }
            body={
              actionable
                ? `Your inbox fills up once leads can reply to you. Connect at least one — ${actionable.label.toLowerCase()} is the fastest to set up.`
                : 'Your inbox fills up once leads can reply to you. None of the reply channels can be connected on this deployment yet; the rows below say why.'
            }
          />
          <div className="ar-stack ar-stack--tight" style={{ marginTop: 'var(--ar-space-4)' }}>
            {channels.map((channel) => (
              <div className="app-conn" key={channel.channel}>
                {/* OUTR-76 / OUTR-111. The logo box the design draws — 2.75rem,
                    inset, the mark centred — and not the 10px channel chip this
                    used to borrow from the thread list. The chip is a *label on
                    a message* and it was being asked to be a brand mark, which
                    is how the setup list came to read "em / wh / li". */}
                <span className="app-conn__logo" aria-hidden="true">
                  {MARKS[channel.channel] ?? channel.label.slice(0, 1)}
                </span>
                <span className="app-conn__body">
                  <strong>{channel.label}</strong>
                  {/* One description line, as drawn (P-INBX-03); the reason a
                      channel cannot be connected is the row's `title`. */}
                  <span title={channel.reason ?? undefined}>{channel.blurb}</span>
                </span>
                {/* A held connection is shown as held, even on the row that
                    can never carry a reply — that is the row the tenant most
                    needs to see as *done*. */}
                {channel.linked && !channel.connected ? (
                  <span className="ar-badge ar-badge--success">✓ Connected</span>
                ) : null}
                {/* OUTR-78. Keyed off *is there anything to connect*, not off
                    *can it carry a message*. LinkedIn fails the second and
                    passes the first: the account is linked for lead discovery
                    and posting, which is most of what §5 asks LinkedIn for, and
                    keying the button off `sendable` left the one row that a
                    tenant most needs to act on as the only row with no way to
                    act. The sentence above still says why no reply will arrive
                    here, so the button promises nothing it cannot do. */}
                {canAct(channel) ? (
                  <Link
                    className={`ar-btn ar-btn--${channel.channel === first ? 'primary' : 'ghost'} ar-btn--sm`}
                    to={channel.setup_path}
                  >
                    {channel.action}
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

/**
 * Whether this row has anything for the tenant to do.
 *
 * **Three ways to have nothing**, and the third was a live defect until it was
 * found reverifying SF-22. The channel already carries replies; or its
 * connection is already held and the remaining obstacle is not the tenant's —
 * LinkedIn having no messaging API; or **there is no tenant-side connection at
 * all**, which is WhatsApp, where AugRank is one shared AugStash tenant and the
 * only question is whether the deployment carries the channel. That last row
 * was drawing a *Connect* button pointing at a Connections screen with no
 * WhatsApp on it — on every deployment without AugStash, which today is all of
 * them, and in the zero state where the button is the whole point of the
 * screen.
 *
 * All three render the server's sentence and no button, because a control whose
 * press changes nothing is worse than its absence.
 *
 * **A fourth since 2026-09-12: the deployment does not carry the channel.**
 * Email is `connectable` (a sender identity is the tenant's to add) and was
 * drawn with *Set up* under *Not available on this deployment yet* — the button
 * led to a card that could not finish. `available` is the deployment's answer
 * and the row keys off it first.
 */
function canAct(channel: Channel): boolean {
  // `available` is about carrying a message, so it gates the sendable rows
  // only: LinkedIn is never available for one and is still the connection §5
  // wants most, because it is what finds the leads (OUTR-78).
  const offered = channel.available || !channel.sendable
  return offered && !channel.connected && channel.connectable && !channel.linked
}

/**
 * The mark in each row's logo box, as `outreach.html#inbox-zero` draws them.
 *
 * Glyphs rather than the vendors' logos, deliberately: WhatsApp's and
 * LinkedIn's marks are trademarks with usage rules, and a 2.75rem inset square
 * with a letter in it is what the design specifies anyway. Unknown channels
 * fall back to the first letter of the server's own label, so a channel added
 * to the catalogue appears here correctly with no change to this file — the
 * same argument the blurbs are server-side for.
 */
const MARKS: Record<string, string> = {
  email: '✉',
  whatsapp: 'W',
  linkedin: 'in',
}

function SyncButton({ onDone }: { onDone: () => void }): JSX.Element {
  const sync = useMutation(() => call(() => api.POST('/api/v1/outreach/inbox/sync', {})))
  return (
    <div className="ar-row">
      <InlineError failure={sync.state.failure} />
      <Button
        tone="ghost"
        size="sm"
        busy={sync.state.status === 'running'}
        onClick={() => {
          void sync.run(undefined).then((result) => {
            if (result.ok) onDone()
          })
        }}
      >
        Refresh
      </Button>
    </div>
  )
}

interface InboxProps {
  conversations: Conversation[]
  /** The whole catalogue, not only what is connected: the one-channel nudge
   *  (OUTR-74) names a channel the tenant does *not* have, and the composer's
   *  sender caption (OUTR-33) reads `sends_from` off the one they do. */
  channels: Channel[]
  connected: string[]
  loading: boolean
  failure: ApiFailure | null
  onRetry: () => void
  search: string
  onSearch: (value: string) => void
  unreadOnly: boolean
  onUnreadOnly: (value: boolean) => void
  category: CategoryFilter | ''
  onCategory: (value: CategoryFilter | '') => void
  /** WEB-1 — whether a newer or older page exists, and how to reach it. */
  hasNewer: boolean
  hasOlder: boolean
  onNewer: () => void
  onOlder: () => void
  canManage: boolean
  selected: string | null
  onSelect: (id: string) => void
  canReply: boolean
  tenantId: string
  onChanged: () => void
  onSent: () => void
}

function Inbox(props: InboxProps): JSX.Element {
  const { conversations, connected, selected } = props
  // One connected channel means the labels carry no information, so they go —
  // S-D4's `#inbox-one`, and the only difference between it and `#inbox`.
  const showLabels = connected.length > 1

  return (
    <div className={selected ? 'app-inbox app-inbox--reading' : 'app-inbox'}>
      <div className="app-inbox__list">
        <div className="app-inbox__filters">
          <input
            className="ar-input"
            type="search"
            placeholder="Search conversations"
            aria-label="Search conversations"
            style={{ height: 'var(--ar-control-sm)' }}
            value={props.search}
            onChange={(event) => {
              props.onSearch(event.target.value)
            }}
          />
          <select
            className="ar-select app-inbox__category"
            aria-label="Filter by reply category"
            value={props.category}
            onChange={(event) => {
              props.onCategory(event.target.value as CategoryFilter | '')
            }}
          >
            <option value="">All replies</option>
            {CATEGORY_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* The Atlas check control, not a bare `<input type="checkbox">`. This
              was the user agent's own checkbox sitting under two token-styled
              controls — the one place in the filter block where the browser
              drew the widget instead of the design system. `.ar-choice` hides
              the input and draws `__box` in its place, so the keyboard and the
              accessibility tree still see a checkbox. */}
          <label className="ar-choice ar-choice--check app-choice--sm app-choice--flush">
            <input
              type="checkbox"
              checked={props.unreadOnly}
              onChange={(event) => {
                props.onUnreadOnly(event.target.checked)
              }}
            />
            <span className="ar-choice__box" aria-hidden="true" />
            <span className="ar-choice__text">Unread only</span>
          </label>
        </div>

        {props.failure ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <ErrorState failure={props.failure} onRetry={props.onRetry} />
          </div>
        ) : props.loading ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <Loading label="Loading conversations" lines={3} />
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: 'var(--ar-space-4)' }}>
            <Empty
              title={props.search || props.category ? 'Nothing matches' : 'No conversations yet'}
              body={
                props.search || props.category
                  ? 'Try a different name, company or phrase — or clear the category filter.'
                  : 'Replies from your leads land here. Press Refresh once a campaign has gone out.'
              }
            />
          </div>
        ) : (
          conversations.map((conversation) => (
            <button
              type="button"
              key={conversation.id}
              className="app-thread"
              aria-current={conversation.id === selected ? 'true' : undefined}
              onClick={() => {
                props.onSelect(conversation.id)
              }}
            >
              {/* The name owns its own line, with the channel and the time on a second.
                  Sharing one line meant the chip and the timestamp took their width
                  first and the name truncated into whatever was left — at 20rem that
                  cut "Priya Sharma" to "Priya …", and the sender is the one thing this
                  list exists to be scanned by. Now only a genuinely long name is
                  ellipsised, and the meta is always visible rather than pushed out
                  through the pane's clipped edge. */}
              <span className="app-thread__top">
                <strong>{conversation.full_name ?? conversation.contact_value}</strong>
                <span className="app-thread__meta">
                  {showLabels && conversation.last_channel ? (
                    <ChannelChip channel={conversation.last_channel} />
                  ) : null}
                  <span className="app-thread__time">{when(conversation.last_message_at)}</span>
                </span>
              </span>
              <span className="app-thread__snip">{conversation.snippet ?? '—'}</span>
              <span className="app-thread__tags">
                <CategoryBadge category={conversation.reply_category ?? null} />
                {/* OUTR-34. Its own badge rather than the mockup's single
                    *"Not interested · suppressed"*, because the two facts can
                    disagree and the interesting case is exactly when they do:
                    somebody who asked to be left alone and is filed as
                    *Interested* is a thread worth opening, and one badge would
                    have to pick which half to tell you. */}
                {conversation.suppressed ? (
                  <span className="ar-badge ar-badge--danger">Suppressed</span>
                ) : null}
                {conversation.unread_count > 0 ? (
                  <span className="ar-badge ar-badge--info">
                    {conversation.unread_count} unread
                  </span>
                ) : null}
              </span>
            </button>
          ))
        )}

        {/* WEB-1. Rendered only when there is somewhere to go, so a tenant with
            one page of conversations never sees a pair of dead controls. */}
        {props.hasNewer || props.hasOlder ? (
          <div
            className="ar-row"
            style={{
              justifyContent: 'space-between',
              gap: 'var(--ar-space-2)',
              padding: 'var(--ar-space-3) var(--ar-space-4)',
            }}
          >
            <Button tone="ghost" size="sm" disabled={!props.hasNewer} onClick={props.onNewer}>
              Newer
            </Button>
            <Button tone="ghost" size="sm" disabled={!props.hasOlder} onClick={props.onOlder}>
              Older
            </Button>
          </div>
        ) : null}
      </div>

      <div className="app-conv app-inbox__conv">
        {selected ? (
          // Keyed, so a different conversation is a different component rather
          // than the same one with new props. An effect that cleared the loaded
          // history on `conversationId` change looked equivalent and was not: a
          // page still in flight when the reader moves on resolves afterwards
          // and prepends one person's history above another's. Unmounting is
          // the only version of this that a late response cannot outrace.
          <ThreadPane
            key={selected}
            conversationId={selected}
            channels={props.channels}
            connected={connected}
            showLabels={showLabels}
            canReply={props.canReply}
            canManage={props.canManage}
            tenantId={props.tenantId}
            onChanged={props.onChanged}
            onSent={props.onSent}
          />
        ) : (
          <div style={{ padding: 'var(--ar-space-5)' }}>
            <Empty
              title="Nothing selected"
              body="Pick a conversation on the left to read it and reply."
            />
          </div>
        )}
      </div>
    </div>
  )
}

function ThreadPane({
  conversationId,
  channels,
  connected,
  showLabels,
  canReply,
  canManage,
  tenantId,
  onChanged,
  onSent,
}: {
  conversationId: string
  channels: Channel[]
  connected: string[]
  showLabels: boolean
  canReply: boolean
  canManage: boolean
  tenantId: string
  onChanged: () => void
  onSent: () => void
}): JSX.Element {
  const toast = useToast()
  const thread = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/conversations/{conversation_id}', {
          params: { path: { conversation_id: conversationId } },
          signal,
        }),
      ),
    [conversationId],
  )

  const markRead = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/outreach/conversations/{conversation_id}/read', {
        params: { path: { conversation_id: id } },
      }),
    ),
  )

  // WEB-2. The thread endpoint is paginated too, and this pane read one page and
  // never `next_cursor`, so a conversation longer than 25 messages showed its
  // newest 25 and no way to see the rest — silently, because the page it did
  // show looked complete.
  //
  // `useMutation` for a `GET`, deliberately: this is a fetch the person asks
  // for by pressing a button, so what it needs is `run`/`pending`/`failure` on
  // demand — which is the mutation hook — rather than `useQuery`'s "run on
  // mount and whenever the deps change".
  const [earlier, setEarlier] = useState<readonly Message[]>([])
  //: The cursor for the next *older* page. `undefined` means "not taken over
  //: yet" — the thread query's own `next_cursor` is still the one to use.
  const [olderCursor, setOlderCursor] = useState<string | null | undefined>(undefined)

  const loadEarlier = useMutation((from: string) =>
    call(() =>
      api.GET('/api/v1/outreach/conversations/{conversation_id}', {
        params: {
          path: { conversation_id: conversationId },
          query: { limit: PAGE_SIZE, cursor: from },
        },
      }),
    ),
  )

  // A different conversation is a different history — enforced by the `key` on
  // this component rather than by an effect here, for the reason given there.
  const resetHistory = useCallback(() => {
    setEarlier([])
    setOlderCursor(undefined)
  }, [])

  const data: Thread | null = thread.state.status === 'ready' ? thread.state.data : null
  const unread = data?.conversation.unread_count ?? 0
  const run = markRead.run

  // Opening a thread is reading it. Fired once per conversation that actually
  // has something unread — not on every render, and not for a thread that is
  // already clear, because a `POST` per keystroke of the search box would be a
  // write on a read.
  //
  // WEB-11: a refused mark had no failure path at all. The badge and the list
  // count then stay stale and the effect will not retry, because `unread` comes
  // from the thread response and does not change. A quiet warning rather than a
  // danger toast — nothing the tenant did failed, and the thread they opened is
  // on screen and readable.
  useEffect(() => {
    if (unread > 0) {
      void run(conversationId).then((result) => {
        if (result.ok) {
          onChanged()
        } else {
          toast.show({
            kind: 'warning',
            title: 'Could not mark this as read',
            body: result.failure.message,
          })
        }
      })
    }
    // `run`, `onChanged` and `toast` are stable for the life of the mutation
    // hook, the page and the provider; including them would re-fire the mark on
    // every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, unread])

  if (thread.state.status === 'loading') return <Loading label="Loading conversation" lines={4} />
  if (thread.state.status === 'error') {
    return <ErrorState failure={thread.state.failure} onRetry={thread.refetch} />
  }
  if (!data) return <Loading label="Loading conversation" lines={4} />

  const person = data.conversation.full_name ?? data.conversation.contact_value
  // OUTR-73. The single connected channel, named as a sentence names it —
  // "WhatsApp", not "WhatsApp Business", and not the raw `whatsapp` id.
  const sole = showLabels ? undefined : channels.find((row) => row.connected)

  // Oldest-first on screen. Each page is already oldest-first within itself
  // (`ThreadResponse` reverses before it goes out) and every page fetched by the
  // cursor is older than everything already here, so an older page goes in
  // front — which is also why the control sits at the top of the list rather
  // than the bottom.
  const messages: readonly Message[] = [...earlier, ...data.messages]
  const nextCursor = olderCursor === undefined ? (data.next_cursor ?? null) : olderCursor

  return (
    <>
      <div className="app-conv__head">
        <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
          {initials(person)}
        </span>
        <div>
          <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>{person}</strong>
          <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            {[data.conversation.company, data.conversation.headline].filter(Boolean).join(' · ') ||
              data.conversation.contact_value}
          </div>
        </div>
        <span className="ar-card__spacer" />
        {/* OUTR-73. With one channel the per-message labels are gone, so the
            head is the one place left that says which channel this
            conversation is on at all — S-D4's `#inbox-one` draws it exactly
            here. Named from the catalogue rather than the connected id, so it
            reads "WhatsApp only" and not "whatsapp only". */}
        {!showLabels && sole !== undefined ? (
          <span className="ar-badge ar-badge--neutral">{sole.short_label} only</span>
        ) : null}
        {/* OUTR-34. Beside the composer it is about to gate, not only on the
            row in the list — somebody who arrived by deep link from a lead has
            never seen the list. */}
        {data.conversation.suppressed ? (
          <span className="ar-badge ar-badge--danger">Suppressed</span>
        ) : null}
        {canManage ? (
          <CategorySelect
            conversationId={conversationId}
            category={data.conversation.reply_category ?? null}
            source={data.conversation.reply_category_source ?? null}
            onChanged={() => {
              thread.refetch()
              onChanged()
            }}
          />
        ) : (
          <CategoryBadge category={data.conversation.reply_category ?? null} />
        )}
        {canManage ? (
          <MoveToWon
            leadId={data.conversation.lead_id}
            stage={data.conversation.stage}
            onMoved={() => {
              thread.refetch()
              onChanged()
            }}
          />
        ) : (
          <span className="ar-badge ar-badge--neutral">{data.conversation.stage}</span>
        )}
      </div>

      <div className="app-conv__body">
        {nextCursor === null ? null : (
          <div
            className="ar-row"
            style={{ justifyContent: 'center', padding: 'var(--ar-space-2)' }}
          >
            <Button
              tone="ghost"
              size="sm"
              busy={loadEarlier.pending}
              busyLabel="Loading"
              onClick={() => {
                void loadEarlier.run(nextCursor).then((result) => {
                  if (!result.ok) return
                  setEarlier((rows) => [...result.data.messages, ...rows])
                  setOlderCursor(result.data.next_cursor ?? null)
                })
              }}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        <InlineError failure={loadEarlier.state.failure} />
        {messages.length === 0 ? (
          <Empty
            title="Nothing here yet"
            body="This conversation has no messages. Press Refresh to pull the latest from your channels."
          />
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`app-msg app-msg--${message.direction}`}>
              {message.body}
              <div className="app-msg__meta">
                {showLabels ? <ChannelChip channel={message.channel} /> : null}
                <span>{when(message.occurred_at)}</span>
                {message.delivery_state ? (
                  <span>· {stateLabel(message.delivery_state)}</span>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      {canReply ? (
        <Composer
          conversationId={conversationId}
          channels={channels}
          connected={connected}
          canSuggest={data.can_suggest}
          preferred={data.conversation.last_channel}
          suppressedAddress={data.conversation.suppressed ? data.conversation.contact_value : null}
          tenantId={tenantId}
          onSent={() => {
            // A sent message shifts the newest page by one, which pushes its
            // oldest message out of that page and into no list at all —
            // `earlier` holds only what was older than the page as it stood.
            // Dropping the loaded history is visible: the control comes back
            // and the reader presses it again. A hole in the middle of a
            // conversation is not.
            resetHistory()
            thread.refetch()
            onChanged()
            onSent()
          }}
        />
      ) : (
        <div className="app-conv__foot">
          <p className="ar-muted" style={{ margin: 0 }}>
            Replying needs an owner or admin — a reply goes to this person in your
            organisation&apos;s name.
          </p>
        </div>
      )}
    </>
  )
}

/* --- reply categories (S-B20, audit rows G17-G19) -------------------------- */

/**
 * The filter's options, as `outreach.html#inbox` draws them.
 *
 * **Five options over four stored values, and the mismatch is deliberate.**
 * *All replies* is the parameter being absent, and *Not classified* selects
 * `NULL` **or** `neutral` — a thread nobody has looked at and one the model read
 * and found nothing actionable in. `app/db/models/tenant.REPLY_CATEGORIES`
 * argues the union: they differ only in whether a model looked, which is a fact
 * for support and not a difference in what the tenant does next.
 */
const CATEGORY_FILTERS: readonly { value: CategoryFilter; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'question', label: 'Question' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unclassified', label: 'Not classified' },
]

/** What a person may set a thread to. `neutral` is here and is not in the filter:
 *  a tenant reading an out-of-office should be able to say so rather than being
 *  pushed into one of the three that imply an intention. */
const CATEGORY_CHOICES: readonly { value: Category; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'question', label: 'Question' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'neutral', label: 'Nothing to act on' },
]

const CATEGORY_TONES: Record<string, string> = {
  interested: 'success',
  question: 'info',
  not_interested: 'warning',
  neutral: 'neutral',
}

const CATEGORY_LABELS: Record<string, string> = {
  interested: 'Interested',
  question: 'Question',
  not_interested: 'Not interested',
  neutral: 'Not classified',
}

/**
 * The label on a thread row.
 *
 * `neutral` renders as **Not classified**, which is the prototype's own reading:
 * it files *"Thanks for connecting."* under that badge, and that is exactly what
 * the classifier calls `neutral`. A tenant has nothing to do about either, and a
 * sixth badge for the difference would be a distinction drawn for the database's
 * benefit rather than the reader's.
 */
function CategoryBadge({ category }: { category: string | null }): JSX.Element {
  if (category === null) return <span className="ar-badge ar-badge--neutral">Not classified</span>
  return (
    <span className={`ar-badge ar-badge--${CATEGORY_TONES[category] ?? 'neutral'}`}>
      {CATEGORY_LABELS[category] ?? category}
    </span>
  )
}

/**
 * G18 — the select that overrules the classifier.
 *
 * §5 requires the category be *"a label you can overrule, never a silent
 * decision that files the thread away"*. Setting it writes
 * `reply_category_source = 'tenant'`, and no later classification touches the
 * thread again — which is what makes overruling it mean something rather than
 * being undone by the next sweep. The screen says which of the two decided it,
 * because *"Haiku thinks"* and *"you said"* are different claims.
 */
function CategorySelect({
  conversationId,
  category,
  source,
  onChanged,
}: {
  conversationId: string
  category: string | null
  source: string | null
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const set = useMutation((next: Category | null) =>
    call(() =>
      api.PUT('/api/v1/outreach/conversations/{conversation_id}/category', {
        params: { path: { conversation_id: conversationId } },
        body: { category: next },
      }),
    ),
  )
  return (
    <span className="app-conv__category">
      <select
        className="ar-select app-conv__categorySelect"
        aria-label="Reply category"
        disabled={set.pending}
        value={category ?? ''}
        onChange={(event) => {
          const value = event.target.value
          void set.run(value === '' ? null : (value as Category)).then((result) => {
            if (result.ok) {
              onChanged()
            } else {
              // WEB-5. The `<select>` is controlled, so a refused change snaps
              // back to the `category` prop on the next render — and did so
              // with no message at all, which reads as the control ignoring the
              // press. A toast rather than an inline block because it has to
              // appear at the moment of the snap-back and this select sits in a
              // flex header a form-summary would break.
              toast.show({
                kind: 'danger',
                title: 'Could not change the category',
                body: result.failure.message,
              })
            }
          })
        }}
      >
        <option value="">Not classified</option>
        {CATEGORY_CHOICES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="ar-subtle app-conv__categoryBy">
        {category === null ? '' : source === 'tenant' ? 'set by you' : 'by Haiku 4.5'}
      </span>
    </span>
  )
}

/**
 * G19 — *Move to Won*, from the conversation that justifies it.
 *
 * On the thread rather than only on the lead, because this is where a tenant is
 * when they decide: the message saying *"send us the contract"* is on screen.
 * Moving to `won` also ends that person's follow-up ladder, which the toast says
 * out loud — a control with a second effect nobody mentioned is one a tenant
 * learns about from a prospect's complaint.
 */
function MoveToWon({
  leadId,
  stage,
  onMoved,
}: {
  leadId: string
  stage: string
  onMoved: () => void
}): JSX.Element {
  const toast = useToast()
  const move = useMutation(() =>
    call(() =>
      api.POST('/api/v1/outreach/leads/{lead_id}/stage', {
        params: { path: { lead_id: leadId } },
        body: { stage: 'won' },
      }),
    ),
  )
  if (stage === 'won') return <span className="ar-badge ar-badge--brand">Won</span>
  return (
    <Button
      tone="ghost"
      size="sm"
      busy={move.pending}
      onClick={() => {
        void move.run(undefined).then((result) => {
          if (result.ok) {
            toast.show({
              kind: 'success',
              title: 'Moved to Won',
              body:
                result.data.follow_ups_cancelled > 0
                  ? 'Their follow-ups have stopped.'
                  : 'Nothing was scheduled for them.',
            })
            onMoved()
          } else {
            // WEB-6. A refused move left the button idle and said nothing, so
            // the stage silently stayed where it was. `LeadDetail.tsx:102-110`
            // has always done this correctly for the same endpoint.
            toast.show({
              kind: 'danger',
              title: 'Could not move to Won',
              body: result.failure.message,
            })
          }
        })
      }}
    >
      Move to Won
    </Button>
  )
}

/**
 * The reply box: a channel to send on, the words, and Send.
 *
 * **The channel is chosen, never inferred.** It defaults to the one the last
 * message arrived on, because that is where the conversation is — but it is a
 * control rather than a rule, since a lead who wrote from a phone may want the
 * rate card by email. Only connected channels are offered, so a channel that
 * would be refused by the server is not offered by the screen.
 */
function Composer({
  conversationId,
  channels,
  connected,
  canSuggest,
  preferred,
  suppressedAddress,
  tenantId,
  onSent,
}: {
  conversationId: string
  channels: Channel[]
  connected: string[]
  /** OUTR-31 — whether this deployment has a model to draft with at all. */
  canSuggest: boolean
  preferred: string | null | undefined
  suppressedAddress: string | null
  tenantId: string
  onSent: () => void
}): JSX.Element {
  // `?? ''` is unreachable: the composer is only rendered when at least one
  // channel is connected. It is there because the type cannot say so, and an
  // assertion that lies is worse than a fallback that never fires.
  const initial = useMemo(
    () => (preferred && connected.includes(preferred) ? preferred : (connected[0] ?? '')),
    [preferred, connected],
  )
  const toast = useToast()
  const storage = useMemo(() => safeStorage(), [])
  const key = draftKey(tenantId, conversationId)
  const [channel, setChannel] = useState(initial)
  // OUTR-32. Read once, on mount — which is once per conversation, because
  // `ThreadPane` is keyed on the selection and takes this component with it.
  // Deliberately *not* an effect: an effect that restored the draft whenever
  // its dependencies changed would overwrite whatever the tenant had typed
  // since, and the one thing a draft must never do is destroy a newer draft.
  const [body, setBody] = useState(() => storage.getItem(key) ?? '')
  const [saved, setSaved] = useState(false)

  // The channel default follows the conversation: switching threads must not
  // leave the composer pointed at the channel the *previous* thread was on.
  // No longer clears the body with it — that is what the draft replaced.
  useEffect(() => {
    setChannel(initial)
  }, [initial])

  // OUTR-32. Autosaved on every change, *and* offered as a button.
  //
  // **Both, and they are not redundant.** The autosave is the safety net: the
  // whole reason a draft exists here is that the reader clicks another
  // conversation half-way through a sentence, and a draft that only survives
  // when somebody remembered to press Save is a draft that is lost exactly when
  // it was needed. The button is the affordance the design draws and the one a
  // person reaches for — it saves, and then *says so*, which is the part the
  // silent write cannot do.
  const write = useCallback(
    (value: string) => {
      if (value.trim()) {
        storage.setItem(key, value)
      } else {
        // An emptied box is not a draft. Removed rather than stored as `""`,
        // because a key per conversation ever opened would grow until it hit
        // the quota — see `safeStorage.removeItem`.
        storage.removeItem(key)
      }
    },
    [storage, key],
  )

  // OUTR-31. What the composer held before a draft replaced it, or `null` when
  // there is nothing to go back to.
  //
  // **The button must not be able to destroy a half-written reply.** Replacing
  // the textarea is the only sensible thing a draft can do — appending one under
  // an unfinished sentence produces nonsense — and a React-controlled `value`
  // takes the browser's own undo stack with it, so Ctrl-Z does not save anybody.
  // One string of state and one quiet control is the whole fix, and it is the
  // same argument the drafts themselves are here for (OUTR-32): text a person
  // typed is the thing this screen must never lose.
  const [replaced, setReplaced] = useState<string | null>(null)
  //: The draft this component last put in the box, so a second press can tell
  //: *"the tenant has not touched it"* from *"they have edited it"*. Without it
  //: `replaced` is overwritten on every press, and pressing the button twice —
  //: which is the normal way somebody asks for a different draft — silently
  //: destroys the sentence they typed before the first one.
  const [drafted, setDrafted] = useState<string | null>(null)

  const suggest = useMutation(() =>
    call(() =>
      api.POST('/api/v1/outreach/suggestions', {
        body: { conversation_id: conversationId },
      }),
    ),
  )

  const send = useMutation((input: { channel: string; body: string }) =>
    call(() =>
      api.POST('/api/v1/outreach/replies', {
        body: { conversation_id: conversationId, channel: input.channel, body: input.body },
        params: {
          header: { 'Idempotency-Key': idempotencyKey('reply', `${tenantId}:${conversationId}`) },
        },
      }),
    ),
  )

  const onSuggest = useCallback(async (): Promise<void> => {
    const result = await suggest.run(undefined)
    // The HTTP failure — a budget refusal above all — is rendered as an
    // `InlineError` below rather than toasted: *"you have reached your cap"*
    // names a number the tenant has to act on, and a toast that has gone by the
    // time they look up is the wrong shape for it.
    if (!result.ok) return
    if (result.data.source !== 'model' || !result.data.text) {
      // The model answered and had nothing to give — a toast, because all three
      // are transient next to the composer they leave untouched. Worded by
      // *which* nothing it was, which is the whole reason the response carries a
      // source rather than an empty string: only one of the three is worth
      // pressing again, and `disabled` will never work on this deployment
      // however many times it is tried.
      toast.show({ kind: 'warning', ...NOTHING_DRAFTED(result.data.source) })
      return
    }
    // What goes into `replaced` is the last thing a **person** wrote, not
    // whatever happened to be in the box. Pressing the button twice is how
    // somebody asks for a different draft, and capturing the first draft on the
    // second press would leave *Undo* restoring the model's words while the
    // tenant's own sentence was gone for good.
    setReplaced((previous) => (body === drafted ? previous : body))
    setDrafted(result.data.text)
    setBody(result.data.text)
    write(result.data.text)
    setSaved(false)
  }, [suggest, toast, body, drafted, write])

  const onSend = useCallback(async (): Promise<void> => {
    if (!body.trim()) return
    const result = await send.run({ channel, body })
    if (!result.ok) return
    setBody('')
    // A sent draft must not come back when the thread is reopened.
    write('')
    setSaved(false)
    // Nor may *Undo* put a sent reply back in the box: the words have gone to
    // the prospect, and offering to restore what they replaced would invite
    // somebody to send the earlier draft on top of it.
    setReplaced(null)
    setDrafted(null)
    onSent()
  }, [body, channel, send, write, onSent])

  // OUTR-74. The nudge names a real channel this tenant could add, taken from
  // the catalogue in its own order — which is *how fast you can be sending on
  // it*, so the one offered is the quickest win rather than the first
  // alphabetically. `null` when there is nothing left to suggest, which is what
  // stops a two-channel tenant being nagged about LinkedIn for ever.
  const missing = channels.find((row) => canAct(row) && row.sendable) ?? null
  // OUTR-33. Who this reply goes out as, for the channel that is actually
  // selected — the caption changes with the segmented control, because that is
  // the whole question it answers. `null` on WhatsApp, where the sender is
  // AugRank's shared AugStash account and naming it would be telling this
  // tenant about another organisation's number.
  const sendsFrom = channels.find((row) => row.channel === channel)?.sends_from ?? null
  // OUTR-108. One connected channel makes the segment a label pretending to be
  // a control: one button, permanently pressed, that does nothing when clicked.
  // S-D4's `#inbox-one` omits it, and so does this.
  const choosing = connected.length > 1
  // The single connected channel, when there is exactly one — what the nudge
  // above is about.
  const only = choosing ? undefined : channels.find((row) => row.connected)
  // …and whether it can hear a reply at all. `receivable` is `false` for email
  // (§5 — AugStash has no inbound parsing, and it is *"not available at any
  // price"*), which makes an email-only inbox one that can never fill up. That
  // is the most useful thing this screen can say and it is true whether or not
  // there is a second channel to offer, so it is tracked separately from
  // `missing` rather than folded into the sentence that names one.
  const deafLine =
    only !== undefined && !only.receivable ? `Replies cannot arrive on ${only.short_label}.` : null

  return (
    <div className="app-conv__foot">
      {/* The design's top row: the channel picker on the left, the drafter on
          the right. Rendered when *either* has something to show — with one
          channel the segment is gone (OUTR-108) and the button still belongs
          here, and on a deployment with no model the row collapses to the
          segment alone. */}
      {choosing || canSuggest ? (
        <div className="ar-row">
          {choosing ? (
            <>
              <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                Reply via
              </span>
              <div className="app-seg" role="group" aria-label="Reply channel">
                {connected.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={option === channel}
                    onClick={() => {
                      setChannel(option)
                    }}
                  >
                    {named(channels, option)}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          <span className="ar-card__spacer" />
          {/* OUTR-31. Absent rather than disabled when this deployment has no
              Anthropic key — `ThreadResponse.can_suggest`. A control that can
              never work is worse than none, which is the same rule the setup
              list applies to a channel that cannot carry a message. */}
          {canSuggest ? (
            <Button
              tone="quiet"
              size="sm"
              busy={suggest.pending}
              busyLabel="Drafting"
              onClick={() => {
                void onSuggest()
              }}
            >
              Suggest a reply
            </Button>
          ) : null}
          {/* One press back to what they had. Only while there is something to
              go back to, and it survives editing the draft — somebody who has
              tidied two words of a suggestion and then decides against it wants
              their own sentence back, not the model's. */}
          {replaced !== null ? (
            <Button
              tone="quiet"
              size="sm"
              onClick={() => {
                setBody(replaced)
                write(replaced)
                setReplaced(null)
                // …and forget the draft, or the next press would read this
                // restored text as an untouched suggestion and decline to
                // capture it.
                setDrafted(null)
                setSaved(false)
              }}
            >
              Undo
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* OUTR-34. Said before the reply is written rather than after it is
          refused. Worded about the address rather than the lead, because it is
          the address that is on the list: the same person may still be
          reachable on a number they have not opted out of, and telling a tenant
          the lead is unreachable when they are not would cost them the deal
          this warning exists to protect. */}
      {suppressedAddress !== null ? (
        <div className="ar-alert ar-alert--warning">
          <span className="ar-alert__icon" aria-hidden="true">
            ⚠
          </span>
          <div className="ar-alert__body">
            {suppressedAddress} is on your suppression list, so nothing further will be sent to it.
            Another channel may still reach this lead.
          </div>
        </div>
      ) : null}

      {/* OUTR-74. Quiet, and a nudge rather than a blocker — S-D4's own words
          for this state. The channel it names and the sentence about it are
          both the catalogue's, so one added later appears here with its own
          reason and no change to this file.

          **The first sentence is the one that matters and it is not decoration.**
          `receivable` is `false` for email (REQUIREMENTS §5 — AugStash has no
          inbound parsing, and it is *"not available at any price"*), which
          means an email-only tenant is looking at an inbox that can never fill
          up. That is the single most useful thing this screen can tell them and
          nothing in the product was telling them. The second wording is for the
          case where replies do arrive and a second channel is merely worth
          having. */}
      {!choosing && (deafLine !== null || missing !== null) ? (
        <div className="ar-alert ar-alert--info">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            {deafLine}{' '}
            {missing !== null ? (
              <>
                {deafLine !== null
                  ? `Connect ${missing.short_label} so leads can answer you.`
                  : `Connect ${missing.short_label} to reply where more leads answer.`}{' '}
                {missing.blurb}.{' '}
                <Link className="ar-link" to={missing.setup_path}>
                  Add a channel
                </Link>
              </>
            ) : (
              // Nothing to offer, and the first sentence is still worth saying —
              // more so, because now there is no way out of it. Rendered without
              // a link rather than with one that goes nowhere, which is the same
              // rule `canAct` applies to the setup list one screen back.
              'No other channel is available on this deployment yet, so answers will not appear here.'
            )}
          </div>
        </div>
      ) : null}

      {/* Its own line, and not a toast: a budget refusal names a ceiling the
          tenant has to act on, and a message that has gone by the time they look
          up is the wrong shape for that. */}
      <InlineError failure={suggest.state.failure} />
      <InlineError failure={send.state.failure} />
      <textarea
        className="ar-textarea"
        style={{ minHeight: '5rem' }}
        placeholder="Write a reply…"
        aria-label="Reply"
        maxLength={4000}
        value={body}
        onChange={(event) => {
          setBody(event.target.value)
          write(event.target.value)
          setSaved(false)
        }}
      />
      <div className="ar-row">
        {/* OUTR-68 / OUTR-110. `tone` stated rather than left to `Button`'s
            default, which is ghost — so the one primary action on the screen
            was rendering as its quietest control. */}
        <Button
          tone="primary"
          busy={send.state.status === 'running'}
          disabled={!body.trim()}
          onClick={() => {
            void onSend()
          }}
        >
          Send
        </Button>
        {/* OUTR-32 / OUTR-69. */}
        <Button
          tone="ghost"
          disabled={!body.trim()}
          onClick={() => {
            write(body)
            setSaved(true)
          }}
        >
          Save draft
        </Button>
        <span className="ar-card__spacer" />
        {sendsFrom !== null ? (
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            Sends from {sendsFrom}
          </span>
        ) : null}
      </div>
      {/* Under the row rather than in it (OUTR-33 took that slot), and it stays:
          §8 makes the unsubscribe link an obligation, and the one place a
          tenant is about to send from is where saying so is worth anything.
          `role="status"` on the draft half so the acknowledgement is announced
          — a caption that only appears is invisible to a screen reader. */}
      <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
        An unsubscribe link is added to every message, as the law requires.{' '}
        <span role="status">{saved ? 'Draft saved on this browser.' : ''}</span>
      </span>
    </div>
  )
}

/**
 * What to say when the drafter gives nothing back, by which nothing it was.
 *
 * Three outcomes with three different next actions, which is the whole reason
 * `SuggestionResponse` carries a `source` rather than an empty string: `error`
 * is worth pressing again, `empty` is worth pressing again *once they write*,
 * and `disabled` will never work on this deployment however many times it is
 * tried. Telling somebody to retry a permanent answer is the specific unkindness
 * this avoids.
 *
 * `disabled` should not be reachable — the button is not drawn when
 * `can_suggest` is `false` — but the flag is read once with the thread and a key
 * can be withdrawn from Parameter Store while somebody has the screen open.
 */
function NOTHING_DRAFTED(source: string): { title: string; body: string } {
  if (source === 'empty') {
    return {
      title: 'Nothing to draft from yet',
      body: 'This conversation has no messages to answer. Once they write, try again.',
    }
  }
  if (source === 'disabled') {
    return {
      title: 'Drafting is not available here',
      body: 'This deployment has no model configured, so replies cannot be drafted.',
    }
  }
  return {
    title: 'Could not draft a reply',
    body: 'The model did not answer. Your reply is exactly as you left it — try again.',
  }
}

/**
 * Where one conversation's unsent reply is kept (OUTR-32).
 *
 * **Namespaced by tenant as well as by conversation.** A conversation id is a
 * UUID and could stand alone, but this is a multi-tenant app on one origin
 * where a person switches organisations without reloading — and a key that
 * carried only the conversation would be a draft written for one customer
 * sitting in the same namespace as another's. The id makes them impossible to
 * confuse and the prefix makes them obvious in a devtools inspector.
 *
 * **Per browser, and the caption says so.** A draft is unsent text, not
 * business data: it is not on the server, so it is not in the tenant's export,
 * not in the audit log, and not on their other laptop. Storing it server-side
 * would mean a table, a migration across every tenant schema and an isolation
 * test for something whose whole life is the ninety seconds between reading a
 * reply and answering it.
 */
function draftKey(tenantId: string, conversationId: string): string {
  return `augrank.inbox.draft.${tenantId}.${conversationId}`
}

function ChannelChip({ channel }: { channel: string }): JSX.Element {
  return <span className={`app-chan app-chan--${channel}`}>{short(channel)}</span>
}

/**
 * What a sentence calls a channel — the server's `short_label`, not a map here.
 *
 * This file used to keep its own `{email: 'Email', whatsapp: 'WhatsApp', …}`,
 * which is a hand-written copy of a server vocabulary and had already drifted:
 * it still listed `sms` and `rcs` five weeks after the catalogue dropped them,
 * and a channel added later would have rendered as its raw id until somebody
 * remembered this constant existed. The fallback is the id for the same reason
 * `MARKS` falls back to a letter — visibly wrong beats invisibly absent.
 */
function named(channels: Channel[], channel: string): string {
  return channels.find((row) => row.channel === channel)?.short_label ?? channel
}

/**
 * The 2–3 character abbreviation on a message chip, which is a different thing
 * and stays here: it is a *visual* compression sized to a 10px pill, not a name
 * — the design draws "WA" and "LI" and the server has no business having an
 * opinion about how many characters fit.
 */
const SHORT: Record<string, string> = {
  email: 'Email',
  whatsapp: 'WA',
  sms: 'SMS',
  rcs: 'RCS',
  linkedin: 'LI',
}

function short(channel: string): string {
  return SHORT[channel] ?? channel
}

/**
 * What a delivery state means in a sentence a tenant can act on.
 *
 * **`suppressed` is the one that matters.** AugStash answers an HTTP success and
 * drops the message, so a screen that showed "sent" for it would be reporting a
 * delivery that never happened — the exact failure `DeliveryState` exists to
 * make impossible. Anything unmapped falls through as itself rather than being
 * hidden: a state this table has not caught up with is still true.
 */
const STATES: Record<string, string> = {
  queued: 'queued',
  deferred: 'held by the provider — it will retry',
  sent: 'sent',
  delivered: 'delivered',
  opened: 'opened',
  clicked: 'clicked',
  read: 'read',
  suppressed: 'not delivered — this person opted out',
  failed: 'failed to send',
  undelivered: 'not delivered',
  bounced: 'bounced',
  complained: 'reported as spam',
}

function stateLabel(state: string): string {
  return STATES[state] ?? state
}

/**
 * A timestamp as the list shows it: a time today, a date otherwise.
 *
 * `Asia/Kolkata` explicitly rather than the viewer's zone (CLAUDE.md): every
 * other date in the product renders in IST, and an inbox that disagreed with
 * the audit log about when a reply arrived would be the one screen somebody
 * cannot reconcile.
 */
function when(value: string | null | undefined): string {
  if (!value) return ''
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
